import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const BUILD_REFRESH_KEY = "app-build-refresh";

const renderApp = () => {
  createRoot(rootElement).render(<App />);
};

function getVersionedUrl(buildId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set("v", buildId);
  return url.toString();
}

async function ensureLatestBuild() {
  if (typeof window === "undefined") return false;

  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "cache-control": "no-cache",
      },
    });

    if (!response.ok) {
      return false;
    }

    const data = (await response.json()) as { buildId?: string };
    const latestBuildId = data.buildId;

    if (!latestBuildId || latestBuildId === __APP_BUILD_ID__) {
      sessionStorage.removeItem(BUILD_REFRESH_KEY);
      return false;
    }

    if (sessionStorage.getItem(BUILD_REFRESH_KEY) === latestBuildId) {
      return false;
    }

    sessionStorage.setItem(BUILD_REFRESH_KEY, latestBuildId);
    window.location.replace(getVersionedUrl(latestBuildId));
    return true;
  } catch (error) {
    console.warn("[Cache] Failed to validate latest build:", error);
    return false;
  }
}

async function clearLegacyPwaCache() {
  if (typeof window === "undefined") return;

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
    }
  } catch (error) {
    console.warn("[Cache] Failed to clear legacy caches:", error);
  }
}

async function bootstrap() {
  const redirectedToLatestBuild = await ensureLatestBuild();
  if (redirectedToLatestBuild) return;

  await clearLegacyPwaCache();
  renderApp();
}

void bootstrap();